import { useFormikContext } from 'formik'
import { ReactElement } from 'react'
import styled from 'styled-components'

import IconButton from 'components/v2/IconButton'
import { AddCircleIcon } from 'components/Icons/AddCircleIcon'
import { CancelIcon } from 'components/Icons/CancelIcon'
import { Input } from 'components/Input'
import { IServicesForm } from 'features/Services/components/NewServicesV2/components/ServicesForm/types'
import { getEmptyService } from 'models/services'
import { verifyFormik } from 'utils/form'
import { IServicesSelectorProps } from './types'
import { isEmpty } from 'lodash'

const SERVICE_INPUT_POSITION = 1

const ServicesRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  width: 100%;
`

const ServicesContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  width: 100%;
`

export const ServiceInput = styled(Input)<{ isActive?: boolean; isServiceValid?: boolean; touched?: boolean }>`
  width: 130px;

  & > div:nth-child(${SERVICE_INPUT_POSITION}) {
    ${({ isActive, isServiceValid, touched }): string => {
      if (isActive) {
        return `
          border: 1px solid #916EE8;
          background-color: #F4F0FF;

          input {
            font-weight: 500;
          }

          svg {
            fill: #916EE8;
          }
        `
      } else if (isServiceValid) {
        return `
          border: 1px solid #00C875;
          background-color: #EEFFFB;

          svg {
            fill: #00C875;
          }
        `
      } else if (touched) {
        return `
          border: 1px solid #FF627B;
          background-color: #FFEAED;

          svg {
            fill: #FF627B;
          }
        `
      }

      return `
        svg {
          fill: #b0b0b0;
        }   
      `
    }}
  }
`

const StopActionContainer = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
`

export const ServicesSelector = ({
  currentServiceIndex,
  setCurrentServiceIndex,
  mainService,
  readOnly
}: IServicesSelectorProps): ReactElement => {
  const { values, setFieldValue, handleChange, handleBlur, touched, errors } = useFormikContext<IServicesForm>()

  const handleClickService = (index: number): void => {
    if (currentServiceIndex !== index) {
      setCurrentServiceIndex(index)
    }
  }

  const handleAddService = (): void => {
    if (mainService) {
      const routeId = mainService.routeId ?? ''
      const newService = getEmptyService(routeId, mainService.stops, mainService.startDate, mainService.endDate)
      setFieldValue('services', [...values.services, newService])
    }
  }

  const handleRemoveService = (index: number): void => {
    setCurrentServiceIndex(0)
    setFieldValue(
      'services',
      values.services.filter((_, i): TSFixMe => i !== index)
    )
  }

  return (
    <ServicesRow>
      <ServicesContainer>
        {values.services.map((service, index): TSFixMe => {
          const isValid = isEmpty(verifyFormik<string>(errors, ['services', `${index}`, 'code']))

          return (
            <ServiceInput
              autoFocus={!readOnly}
              key={`service-${index}`}
              id={`services[${index}].code`}
              name={`services[${index}].code`}
              value={service.code}
              onClick={(): void => handleClickService(index)}
              onChange={(event): void => {
                if (!readOnly) handleChange(event)
              }}
              onBlur={handleBlur}
              touched={verifyFormik<boolean>(touched, ['services', `${index}`, 'code'])}
              errors={verifyFormik<string>(errors, ['services', `${index}`, 'code'])}
              isServiceValid={isValid}
              isActive={currentServiceIndex === index}
              iconRight={
                <>
                  {values.services.length > 1 && !readOnly && (
                    <StopActionContainer
                      onClick={(e): void => {
                        e.stopPropagation()
                        handleRemoveService(index)
                      }}
                    >
                      <CancelIcon />
                    </StopActionContainer>
                  )}
                </>
              }
              small
            />
          )
        })}
      </ServicesContainer>
      {!readOnly && <IconButton icon={<AddCircleIcon />} onClick={handleAddService} />}
    </ServicesRow>
  )
}
